import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ProgresoDashboard } from '../ProgresoDashboard';
import { Modal, ConfirmacionAprobacionServicio, FeedbackModal, ResultadoGestionModal, ModalCorreoTemporal } from '../Modal';
import { GestionCupsModal } from '../GestionCupsModal';
import { useCupsGestion } from '../../hooks/useCupsGestion';
import { useAuditoria } from '../../hooks/useAuditoria';
import CheckIcon from '../Icons/Check.icon';
import SafeHtmlContent from '../Common/SafeHtmlContent';
import { FEATURE_NOTIFICACION_CORREO_HABILITADA } from '../../featureFlags';
import './gestionCups.css';

interface Anexo {
  Id: number;
  Nombre: string;
  FechaFin: string;
  FechaInicio: string;
  OtrosAspectos: string;
  TipoAcuerdo: string;
}

interface Contrato {
  Id: number;
  Nombre: string;
  Anexos: Anexo[];
}

interface ServicioDetalle {
  Id: number;
  Servicio: string;
  CantidadCups: number;
  CantidadPendientes: number;
  Encabezado?: string | null;
  Observaciones?: string | null;
}

interface GestionCupsProps {
  contrato: Contrato | null;
  anexo: Anexo | null;
  onResetear?: () => void;
}

const GestionCups: React.FC<GestionCupsProps> = ({ contrato, anexo, onResetear }) => {
  // Estados para el modal de resultado de gestión (definir ANTES del hook)
  const [showResultadoGestionModal, setShowResultadoGestionModal] = useState(false);
  const [resultadoGestion, setResultadoGestion] = useState<{
    tipo: 'aprobado' | 'rechazado';
    porcentaje: number;
    totalRechazados: number;
    correoDestino?: string;
    estadoNotificacion?: string;
    mensajeNotificacion?: string;
  }>({ tipo: 'aprobado', porcentaje: 0, totalRechazados: 0 });

  // Estados para el modal de solicitud de correo temporal
  const [showModalCorreoTemporal, setShowModalCorreoTemporal] = useState(false);
  const [procesandoCorreoTemporal, setProcesandoCorreoTemporal] = useState(false);
  const continuarNotificacionRef = useRef<((correo: string) => Promise<void>) | null>(null);

  // Callback para mostrar el modal de resultado
  const handleMostrarResultadoGestion = useCallback((
    tipo: 'aprobado' | 'rechazado', 
    porcentaje: number, 
    totalRechazados: number,
    correoDestino?: string,
    estadoNotificacion?: string,
    mensajeNotificacion?: string
  ) => {
    setResultadoGestion({ 
      tipo, 
      porcentaje, 
      totalRechazados,
      correoDestino,
      estadoNotificacion,
      mensajeNotificacion
    });
    setShowResultadoGestionModal(true);
    
    // Cerrar el modal de gestión de CUPS si está abierto
    setShowCupsModal(false);
    setServicioSeleccionadoModal(null);
    setCupsModalStep('gestion');
  }, []);


  // Callback para solicitar correo cuando está vacío o inválido al finalizar la gestión
  const handleSolicitarCorreoAprobador = useCallback((anexoId: number, continuarConCorreo: (correo: string) => Promise<void>) => {
    // Cerrar cualquier modal de gestión de CUPS o confirmación que pudiera estar abierto
    setShowCupsModal(false);
    setShowConfirmacionModal(false);
    setShowDetallesModal(false);
    setServicioSeleccionadoModal(null);
    setCupsModalStep('gestion');

    continuarNotificacionRef.current = continuarConCorreo;
    setShowModalCorreoTemporal(true);
  }, []);

  const handleConfirmarCorreoTemporal = async (correo: string) => {
    setProcesandoCorreoTemporal(true);
    try {
      // Actualizar localStorage con el nuevo correo para futuras acciones
      const aprobadorInfoStr = localStorage.getItem('aprobador-info');
      if (aprobadorInfoStr) {
        try {
          const aprobador = JSON.parse(aprobadorInfoStr);
          aprobador.correo = correo;
          localStorage.setItem('aprobador-info', JSON.stringify(aprobador));
        } catch (e) {
          console.error('Error al actualizar aprobador-info:', e);
        }
      }

      // Cerrar modal de correo antes de disparar la notificación para evitar solapamiento con ResultadoGestionModal
      setShowModalCorreoTemporal(false);

      if (continuarNotificacionRef.current) {
        await continuarNotificacionRef.current(correo);
      }
      continuarNotificacionRef.current = null;
    } catch (error) {
      console.error('Error al notificar con correo temporal:', error);
    } finally {
      setProcesandoCorreoTemporal(false);
    }
  };


  const {
    servicios,
    progresoGestion,
    loading,
    cargarServicios,
    actualizarEstadoCups,
    validarRespuestaServicio,
    programarRecargaDiferida
  } = useCupsGestion(
    anexo?.Id,
    handleMostrarResultadoGestion,
    anexo?.Nombre,
    anexo?.Id ? anexo.Id.toString() : undefined,
    FEATURE_NOTIFICACION_CORREO_HABILITADA ? handleSolicitarCorreoAprobador : undefined
  );


  const { registrarAuditoria } = useAuditoria();

  const [serviciosSeleccionados, setServiciosSeleccionados] = useState<number[]>([]);
  const [procesando] = useState(false);
  
  // Estados para el modal de gestión de CUPS
  const [showCupsModal, setShowCupsModal] = useState(false);
  const [servicioSeleccionadoModal, setServicioSeleccionadoModal] = useState<{ id: number; nombre: string } | null>(null);
  const [cupsModalStep, setCupsModalStep] = useState<'gestion' | 'confirmacion-aprobacion' | 'confirmacion-rechazo' | 'feedback'>('gestion');
  
  // Estados para confirmación de CUPS
  const [cupsParaAprobar, setCupsParaAprobar] = useState<{id: number; cups: string; descripcion: string}[]>([]);
  
  // Estados para el modal de detalles del servicio
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [servicioDetalles, setServicioDetalles] = useState<ServicioDetalle | null>(null);
  
  // Estados para el modal de confirmación de aprobación
  const [showConfirmacionModal, setShowConfirmacionModal] = useState(false);
  const [serviciosParaAprobar, setServiciosParaAprobar] = useState<{ id: number; nombre: string }[]>([]);
  const [procesandoConfirmacion, setProcesandoConfirmacion] = useState(false);

  // Estados para el feedback modal
  const [feedbackTipo, setFeedbackTipo] = useState<'exito' | 'error'>('exito');
  const [feedbackMensaje, setFeedbackMensaje] = useState('');
  const [feedbackContexto, setFeedbackContexto] = useState<'cups' | 'servicios'>('cups');
  const [limpiarSeleccionCups, setLimpiarSeleccionCups] = useState(false);
  const [procesandoCupsConfirmacion, setProcesandoCupsConfirmacion] = useState(false);
  
  // Estado para rastrear si se han hecho cambios en la sesión del modal
  const [huboOperacionesCups, setHuboOperacionesCups] = useState(false);

  // Estado para mantener los totales actuales del modal de CUPS
  const [totalesCupsModal, setTotalesCupsModal] = useState({
    totalCups: 0,
    totalAprobados: 0,
    totalPendientes: 0,
    totalRechazados: 0
  });

  // Función helper para verificar cierre de modal basado en respuesta del servidor
  const verificarCierreModalConRespuesta = useCallback(async (
    response: { Result: { IdsOk: number } }, 
    cupsGestionados: number[], 
    totalPendientesAntes: number
  ) => {
    const cupsExitosos = response.Result.IdsOk || 0;
    const cupsPendientesRestantes = totalPendientesAntes - cupsExitosos;
    
    if (cupsPendientesRestantes <= 0) {
      setTimeout(() => {
        setShowCupsModal(false);
        setCupsModalStep('gestion');
      }, 1500);
    }
    
    setTimeout(() => {
      // Ya no llamamos cargarServicios manualmente porque 
      // el hook maneja las recargas automáticamente después de operaciones exitosas
    }, 2000);
  }, []);

  // Función para recibir los totales del modal de CUPS
  const handleTotalesChange = React.useCallback((totales: typeof totalesCupsModal) => {
    setTotalesCupsModal(totales);
  }, []);

  // Scroll hacia la sección cuando se selecciona un anexo
  useEffect(() => {
    if (anexo) {
      setTimeout(() => {
        try {
          const gestionElement = document.querySelector('.gestion-cups-container');
          if (gestionElement) {
            gestionElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        } catch (error) {
          console.warn('Error al hacer scroll a la sección de gestión CUPS:', error);
        }
      }, 100);
    }
  }, [anexo]);

  // Limpiar servicios seleccionados que ya no son elegibles para aprobación completa
  useEffect(() => {
    if (servicios.length > 0 && serviciosSeleccionados.length > 0) {
      const serviciosValidosSeleccionados = serviciosSeleccionados.filter(servicioId => {
        const servicio = servicios.find(s => s.Id === servicioId);
        return servicio && servicio.CantidadPendientes > 0 && servicio.CantidadRechazados === 0;
      });
      
      if (serviciosValidosSeleccionados.length !== serviciosSeleccionados.length) {
        setServiciosSeleccionados(serviciosValidosSeleccionados);
      }
    }
  }, [servicios, serviciosSeleccionados]);

  if (!contrato || !anexo) {
    return null;
  }

  const ciudadUsuarioAuditoria =
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('CiudadUsuario') : null) || 'N/A';

  const puedeAprobarServicioCompleto = (servicio: Servicio) => {
    return servicio.CantidadPendientes > 0 && servicio.CantidadRechazados === 0;
  };

  const handleServicioSeleccionado = (servicioId: number, isSelected: boolean) => {
    const servicio = servicios.find(s => s.Id === servicioId);
    if (!servicio || !puedeAprobarServicioCompleto(servicio)) {
      return;
    }

    if (isSelected) {
      setServiciosSeleccionados(prev => [...prev, servicioId]);
    } else {
      setServiciosSeleccionados(prev => prev.filter(id => id !== servicioId));
    }
  };

  const aprobarServiciosSeleccionados = () => {
    if (serviciosSeleccionados.length === 0) {
      return;
    }
    
    const serviciosParaAprobar = serviciosSeleccionados.map(id => {
      const servicio = servicios.find(s => s.Id === id);
      return servicio && puedeAprobarServicioCompleto(servicio) ? { id: servicio.Id, nombre: servicio.Servicio } : null;
    }).filter(Boolean) as { id: number; nombre: string }[];
    
    if (serviciosParaAprobar.length === 0) {
      return;
    }
    
    setServiciosParaAprobar(serviciosParaAprobar);
    setShowConfirmacionModal(true);
  };
  
  const confirmarAprobacion = async () => {
    if (serviciosSeleccionados.length === 0) return;

    setProcesandoConfirmacion(true);

    try {
      const result = await actualizarEstadoCups(
        serviciosSeleccionados,
        'CUPS_SERVICIOS',
        'SI'
      );

      const validacion = validarRespuestaServicio(result, serviciosSeleccionados, 'CUPS_SERVICIOS');

      const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();
      registrarAuditoria({
        transaccion: 'Anexos tarifarios', // TODO: confirmar con equipo
        programa: 'Proveedores',              // TODO: confirmar con equipo
        tipoIdUsuario: _aAprobador?.userTypeId,
        numIdUsuario: _aAprobador?.userId,
        nombreUsuario: _aAprobador?.nombre,
        tipoIdAfiliado: _aAprobador?.userTypeId,
        numIdAfiliado: _aAprobador?.userId,
        ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Aprobación Servicio CUPS', // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: _aAprobador?.roles ?? 'N/A',
          Sede: _aAprobador?.sede ?? 'Sede no asociada',
          CorreoElectronico: _aAprobador?.correo,
          Resultado: validacion.esExitoso ? 'exitoso' : 'no exitoso',
          MensajeError: validacion.esExitoso ? '' : validacion.mensaje,
        },
      });

      if (validacion.esExitoso) {
        const nombresServicios = serviciosParaAprobar.map(s => s.nombre).join(', ');
        setFeedbackTipo('exito');
        setFeedbackMensaje(`${serviciosParaAprobar.length === 1 ? 'El servicio' : 'Los servicios'} ${nombresServicios} ${serviciosParaAprobar.length === 1 ? 'fue aprobado' : 'fueron aprobados'} con éxito`);
        setFeedbackContexto('servicios');
        
        // Cerrar el modal de confirmación
        setShowConfirmacionModal(false);
        setProcesandoConfirmacion(false);
        
        // Mostrar feedback
        setCupsModalStep('feedback');
        setShowCupsModal(true);
        
        await cargarServicios();
        setServiciosSeleccionados([]);
        setServiciosParaAprobar([]);
      } else {
        setFeedbackTipo('error');
        setFeedbackMensaje(validacion.mensaje);
        setFeedbackContexto('servicios');
        
        // Cerrar el modal de confirmación
        setShowConfirmacionModal(false);
        setProcesandoConfirmacion(false);
        
        // Mostrar feedback
        setCupsModalStep('feedback');
        setShowCupsModal(true);
      }
        
    } catch (error) {
      console.error('Error en la petición:', error);

      const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();
      registrarAuditoria({
        transaccion: 'Anexos tarifarios', // TODO: confirmar con equipo
        programa: 'Proveedores',              // TODO: confirmar con equipo
        tipoIdUsuario: _aAprobador?.userTypeId,
        numIdUsuario: _aAprobador?.userId,
        nombreUsuario: _aAprobador?.nombre,
        tipoIdAfiliado: _aAprobador?.userTypeId,
        numIdAfiliado: _aAprobador?.userId,
        ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Aprobación Servicio CUPS', // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: _aAprobador?.roles ?? 'N/A',
          Sede: _aAprobador?.sede ?? 'Sede no asociada',
          CorreoElectronico: _aAprobador?.correo,
          Resultado: 'no exitoso',
          MensajeError: 'Error de conexión al servicio ActualizarEstadoCupsTarifario',
        },
      });

      setFeedbackTipo('error');
      setFeedbackMensaje('Error de conexión. Por favor, verifica tu conexión a internet e inténtalo de nuevo.');
      setFeedbackContexto('servicios');
      
      // Cerrar el modal de confirmación
      setShowConfirmacionModal(false);
      setProcesandoConfirmacion(false);
      
      // Mostrar feedback
      setCupsModalStep('feedback');
      setShowCupsModal(true);
    }
  };
  
  const cancelarAprobacion = () => {
    if (!procesandoConfirmacion) {
      setShowConfirmacionModal(false);
      setServiciosParaAprobar([]);
    }
  };

  const toggleDetallesServicio = (servicioId: number) => {
    const servicio = servicios.find(s => s.Id === servicioId);
    if (servicio) {
      setServicioDetalles(servicio);
      setShowDetallesModal(true);
    }
  };

  const gestionarCupsServicio = (servicioId: number) => {
    console.log('Gestionar CUPS del servicio:', servicioId);
    
    const servicio = servicios.find(s => s.Id === servicioId);
    if (servicio) {
      setServicioSeleccionadoModal({
        id: servicioId,
        nombre: servicio.Servicio
      });
      setHuboOperacionesCups(false);
      setShowCupsModal(true);
    }
  };

  const cerrarCupsModal = async () => {
    setShowCupsModal(false);
    setServicioSeleccionadoModal(null);
    setCupsModalStep('gestion');
    
    if (huboOperacionesCups) {
      // Ya no usamos cargarServicios directo, el hook maneja las recargas automáticamente
      setHuboOperacionesCups(false);
    }
  };

  const manejarCierreModal = () => {
    if (cupsModalStep === 'gestion') {
      cerrarCupsModal();
    } else if (cupsModalStep === 'confirmacion-aprobacion' || cupsModalStep === 'confirmacion-rechazo') {
      cancelarConfirmacionCups();
    } else if (cupsModalStep === 'feedback') {
      cerrarFeedbackCups();
    }
  };

  const handleShowCupsConfirmation = (cups: {id: number; cups: string; descripcion: string}[]) => {
    setCupsParaAprobar(cups);
    setCupsModalStep('confirmacion-aprobacion');
  };

  const handleShowCupsRechazo = (cups: {id: number; cups: string; descripcion: string}[]) => {
    setCupsParaAprobar(cups);
    setCupsModalStep('confirmacion-rechazo');
  };

  const cancelarConfirmacionCups = () => {
    setCupsModalStep('gestion');
    setCupsParaAprobar([]);
  };

  const cerrarFeedbackCups = async () => {
    setLimpiarSeleccionCups(true);
    
    // Solo recargar manualmente si es feedback de servicios
    // Las operaciones de CUPS se manejan automáticamente por el hook
    if (feedbackContexto === 'servicios') {
      // Para servicios, usamos recarga diferida en lugar de inmediata
      programarRecargaDiferida();
    }
    // Para CUPS NO establecemos recargarDatosCups, el hook ya maneja la recarga automáticamente
    
    if (feedbackContexto === 'cups') {
      setCupsModalStep('gestion');
    } else {
      setShowCupsModal(false);
      setCupsModalStep('gestion');
    }
    
    setFeedbackMensaje('');
    setFeedbackContexto('cups');
    
    setTimeout(() => {
      setLimpiarSeleccionCups(false);
    }, 100);
  };

  const confirmarAprobacionCups = async () => {
    try {
      const cupsIds = cupsParaAprobar.map(cup => cup.id);

      const result = await actualizarEstadoCups(cupsIds, 'CUPS', 'SI');

      const validacion = validarRespuestaServicio(result, cupsIds, 'CUPS');

      const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();
      registrarAuditoria({
        transaccion: 'Anexos tarifarios',  // TODO: confirmar con equipo
        programa: 'Proveedores',               // TODO: confirmar con equipo
        tipoIdUsuario: _aAprobador?.userTypeId,
        numIdUsuario: _aAprobador?.userId,
        nombreUsuario: _aAprobador?.nombre,
        tipoIdAfiliado: _aAprobador?.userTypeId,
        numIdAfiliado: _aAprobador?.userId,
        ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Aprobación CUPS Individual', // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: _aAprobador?.roles ?? 'N/A',
          Sede: _aAprobador?.sede ?? 'Sede no asociada',
          CorreoElectronico: _aAprobador?.correo,
          Resultado: validacion.esExitoso ? 'exitoso' : 'no exitoso',
          MensajeError: validacion.esExitoso ? '' : validacion.mensaje,
        },
      });

      if (validacion.esExitoso) {
        setHuboOperacionesCups(true);
        
        setFeedbackTipo('exito');
        if (cupsParaAprobar.length === 1) {
          const cupsValue = cupsParaAprobar[0].cups;
          setFeedbackMensaje(`El CUPS ${cupsValue} fue aprobado con éxito`);
        } else {
          setFeedbackMensaje(`Los CUPS seleccionados fueron aprobados con éxito`);
        }
        setFeedbackContexto('cups');
        
        setCupsModalStep('feedback');
        setCupsParaAprobar([]);
        
        // Verificar cierre inmediato basado en la respuesta del servidor
        await verificarCierreModalConRespuesta(result, cupsIds, totalesCupsModal.totalPendientes);
      } else {
        setFeedbackTipo('error');
        setFeedbackMensaje(validacion.mensaje);
        setCupsModalStep('feedback');
      }
      
    } catch (error) {
      console.error('Error al aprobar CUPS:', error);

      const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();
      registrarAuditoria({
        transaccion: 'Anexos tarifarios',  // TODO: confirmar con equipo
        programa: 'Proveedores',               // TODO: confirmar con equipo
        tipoIdUsuario: _aAprobador?.userTypeId,
        numIdUsuario: _aAprobador?.userId,
        nombreUsuario: _aAprobador?.nombre,
        tipoIdAfiliado: _aAprobador?.userTypeId,
        numIdAfiliado: _aAprobador?.userId,
        ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Aprobación CUPS Individual', // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: _aAprobador?.roles ?? 'N/A',
          Sede: _aAprobador?.sede ?? 'Sede no asociada',
          CorreoElectronico: _aAprobador?.correo,
          Resultado: 'no exitoso',
          MensajeError: 'Error de conexión al servicio ActualizarEstadoCupsTarifario',
        },
      });

      setFeedbackTipo('error');
      setFeedbackMensaje('Error de conexión. Por favor, inténtalo de nuevo.');
      setCupsModalStep('feedback');
    }
  };

  const confirmarRechazoCups = async (motivo: string) => {
  try {
    const cupsIds = cupsParaAprobar.map(cup => cup.id);

    const result = await actualizarEstadoCups(cupsIds, 'CUPS', 'NO', motivo);

    const validacion = validarRespuestaServicio(result, cupsIds, 'CUPS');

    const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();

    registrarAuditoria({
      transaccion: 'Anexos tarifarios',
      programa: 'Proveedores',
      tipoIdUsuario: _aAprobador?.userTypeId,
      numIdUsuario: _aAprobador?.userId,
      nombreUsuario: _aAprobador?.nombre,
      tipoIdAfiliado: _aAprobador?.userTypeId,
      numIdAfiliado: _aAprobador?.userId,
      ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
      datosAdicionales: {
        Accion: 'Rechazo CUPS Individual',
        NumeroRadicado: 'N/A',
        Adjuntos: 'N/A',
        EmpresaCreada: 'N/A',
        ClienteCreado: 'N/A',
        ClienteVinculado: 'N/A',
        CiudadUsuario: ciudadUsuarioAuditoria,
        Rol: _aAprobador?.roles ?? 'N/A',
        Sede: _aAprobador?.sede ?? 'Sede no asociada',
        CorreoElectronico: _aAprobador?.correo,
        Resultado: validacion.esExitoso ? 'exitoso' : 'no exitoso',
        MensajeError: validacion.esExitoso ? '' : validacion.mensaje,
      },
    });

    if (validacion.esExitoso) {
      setHuboOperacionesCups(true);

      setFeedbackTipo('exito');
      setFeedbackMensaje(
        'La información fue enviada correctamente y se encuentra en procesamiento.'
      );
      setFeedbackContexto('cups');

      setCupsModalStep('feedback');
      setCupsParaAprobar([]);

      await verificarCierreModalConRespuesta(result, cupsIds, totalesCupsModal.totalPendientes);
    } else {
      setFeedbackTipo('error');
      setFeedbackMensaje(validacion.mensaje);
      setCupsModalStep('feedback');
    }

  } catch (error) {
    console.error('Error al rechazar CUPS:', error);

    const _aAprobador = (() => { try { return JSON.parse(localStorage.getItem('aprobador-info') ?? 'null'); } catch { return null; } })();

    registrarAuditoria({
      transaccion: 'Anexos tarifarios',
      programa: 'Proveedores',
      tipoIdUsuario: _aAprobador?.userTypeId,
      numIdUsuario: _aAprobador?.userId,
      nombreUsuario: _aAprobador?.nombre,
      tipoIdAfiliado: _aAprobador?.userTypeId,
      numIdAfiliado: _aAprobador?.userId,
      ipUsuario: _aAprobador?.ip ?? '192.168.1.1',
      datosAdicionales: {
        Accion: 'Rechazo CUPS Individual',
        NumeroRadicado: 'N/A',
        Adjuntos: 'N/A',
        EmpresaCreada: 'N/A',
        ClienteCreado: 'N/A',
        ClienteVinculado: 'N/A',
        CiudadUsuario: ciudadUsuarioAuditoria,
        Rol: _aAprobador?.roles ?? 'N/A',
        Sede: _aAprobador?.sede ?? 'Sede no asociada',
        CorreoElectronico: _aAprobador?.correo,
        Resultado: 'no exitoso',
        MensajeError: 'Error de conexión al servicio ActualizarEstadoCupsTarifario',
      },
    });

    setFeedbackTipo('error');
    setFeedbackMensaje('Error de conexión. Por favor, inténtalo de nuevo.');
    setCupsModalStep('feedback');
  }
};

  return (
    <div className="gestion-cups-container">
      <div className="gap-40">
        <div className="contenedor activo">
          <div className="numbercontainer">
            <span className="digit">2</span>
          </div>
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#333333ff', width: '100%' }}>
              Aprueba el <strong>servicio completo</strong> o gestiona sus CUPS individualmente desde la opción <strong>Gestionar CUPS</strong>.
            </p>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Cargando servicios...</p>
              </div>
            ) : servicios.length > 0 ? (
              <div style={{ marginTop: '20px', width: '100%' }}>
                
                {/* Tabla de servicios */}
                <div className="servicios-table-container">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}></th>
                        <th>Servicio</th>
                        <th style={{ textAlign: 'center' }}>Cantidad de CUPS</th>
                        <th style={{ textAlign: 'center' }}>Observaciones</th>
                        <th style={{ textAlign: 'center' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicios.map((servicio, index) => (
                        <React.Fragment key={servicio.Id}>
                          <tr>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={serviciosSeleccionados.includes(servicio.Id)}
                                onChange={(e) => handleServicioSeleccionado(servicio.Id, e.target.checked)}
                                className="servicio-checkbox"
                                disabled={!puedeAprobarServicioCompleto(servicio)}
                                title={servicio.CantidadRechazados > 0 ? 'No se puede aprobar el servicio completo porque tiene CUPS rechazados.' : ''}
                              />
                            </td>
                            <td>{servicio.Servicio}</td>
                            <td style={{ textAlign: 'center' }}>{servicio.CantidadCups}</td>
                            <td style={{ textAlign: 'center' }}>
                              {servicio.Observaciones ? (
                                <button 
                                  type="button"
                                  className="btn-detalles-servicio"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleDetallesServicio(servicio.Id);
                                  }}
                                >
                                  Detalles del servicio
                                </button>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button 
                                type="button"
                                className={`btn-gestionar-cups ${servicio.CantidadPendientes > 0 ? 'enabled' : 'disabled'}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (servicio.CantidadPendientes > 0) {
                                    gestionarCupsServicio(servicio.Id);
                                  }
                                }}
                                disabled={servicio.CantidadPendientes === 0}
                              >
                                <span className="icon-edit"></span>
                                Gestionar CUPS
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Botón de aprobar seleccionado */}
                <div style={{ 
                  marginTop: '20px', 
                  display: 'flex', 
                  justifyContent: 'center' 
                }}>
                  <button
                    type="button"
                    className={`btn-aprobar-seleccionados ${serviciosSeleccionados.length > 0 ? 'enabled' : 'disabled'}`}
                    onClick={(e) => {
                      aprobarServiciosSeleccionados();
                    }}
                    disabled={serviciosSeleccionados.length === 0 || procesando}
                  >
                    <CheckIcon style={{ width: '16px', height: '16px' }} />
                    {procesando ? 'Procesando...' : `Aprobar${serviciosSeleccionados.length > 0 ? ` (${serviciosSeleccionados.length})` : ''}`}
                  </button>
                </div>

                {/* Sección de progreso */}
                {progresoGestion && (
                  <ProgresoDashboard progresoGestion={progresoGestion} />
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No se encontraron servicios para este anexo.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de gestión de CUPS */}
      <Modal
        isOpen={showCupsModal}
        title={cupsModalStep === 'confirmacion-aprobacion' ? 'Aprobación de CUPS' : 
               cupsModalStep === 'confirmacion-rechazo' ? 'Rechazo de CUPS' : 
               cupsModalStep === 'feedback' ? '' :
               `Gestión de CUPS - ${servicioSeleccionadoModal?.nombre || 'Servicio'}`}
        onClose={manejarCierreModal}
        closeOnBackdropClick={!procesandoCupsConfirmacion}
        variant={cupsModalStep !== 'gestion' ? 'compact' : 'wide'}
        showCloseButton={cupsModalStep !== 'feedback' && !procesandoCupsConfirmacion}
      >
        {cupsModalStep === 'feedback' ? (
          <FeedbackModal
            tipo={feedbackTipo}
            mensaje={feedbackMensaje}
            onClose={cerrarFeedbackCups}
          />
        ) : (
          servicioSeleccionadoModal && (
            <GestionCupsModal
              servicioId={servicioSeleccionadoModal.id}
              servicioNombre={servicioSeleccionadoModal.nombre}
              onClose={cerrarCupsModal}
              step={cupsModalStep}
              cupsParaAprobar={cupsParaAprobar}
              onShowConfirmation={handleShowCupsConfirmation}
              onShowRechazo={handleShowCupsRechazo}
              onCancelarConfirmacion={cancelarConfirmacionCups}
              onConfirmarAprobacion={confirmarAprobacionCups}
              onConfirmarRechazo={confirmarRechazoCups}
              feedbackTipo={feedbackTipo}
              feedbackMensaje={feedbackMensaje}
              onCerrarFeedback={cerrarFeedbackCups}
              limpiarSeleccion={limpiarSeleccionCups}
              onTotalesChange={handleTotalesChange}
              onProcesandoChange={(procesando) => setProcesandoCupsConfirmacion(procesando)}
            />
          )
        )}
      </Modal>

      {/* Modal de confirmación de aprobación */}
      <Modal
        isOpen={showConfirmacionModal}
        title="Confirmar Aprobación"
        onClose={cancelarAprobacion}
        closeOnBackdropClick={false}
        variant="default"
        showCloseButton={!procesandoConfirmacion}
      >
        {serviciosParaAprobar.length > 0 && (
          <ConfirmacionAprobacionServicio
            servicios={serviciosParaAprobar}
            onConfirmarAprobacion={confirmarAprobacion}
            onCancelar={cancelarAprobacion}
            procesando={procesandoConfirmacion}
          />
        )}
      </Modal>

      {/* Modal de detalles del servicio */}
      <Modal
        isOpen={showDetallesModal}
        title={`Servicio ${servicioDetalles?.Servicio || ''}`}
        onClose={() => setShowDetallesModal(false)}
        closeOnBackdropClick={true}
        variant="default"
      >
        {servicioDetalles && (
          <div className="detalle-servicio-modal">
            <div className="detalle-content">
              {servicioDetalles.Encabezado && (
                <SafeHtmlContent
                  htmlContent={servicioDetalles.Encabezado}
                  className="detalle-html-content"
                />
              )}
              
              {servicioDetalles.Observaciones && (
                <SafeHtmlContent
                  htmlContent={servicioDetalles.Observaciones}
                  className="detalle-html-content"
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de resultado de gestión */}
      <Modal
        isOpen={showResultadoGestionModal}
        title=""
        onClose={() => {
          setShowResultadoGestionModal(false);
          // Recargar información cuando se cierra
          cargarServicios();
        }}
        closeOnBackdropClick={false}
        showCloseButton={false}
        variant="default"
      >
        <ResultadoGestionModal
          tipo={resultadoGestion.tipo}
          porcentajeRechazados={resultadoGestion.porcentaje}
          totalRechazados={resultadoGestion.totalRechazados}
          correoDestino={resultadoGestion.correoDestino}
          estadoNotificacion={resultadoGestion.estadoNotificacion}
          mensajeNotificacion={resultadoGestion.mensajeNotificacion}
          onClose={() => {
            setShowResultadoGestionModal(false);
            // Resetear formularios y recargar contratos
            if (onResetear) {
              onResetear();
            }
          }}
        />

      </Modal>

      {/* Modal para solicitar correo temporal si está vacío o es inválido */}
      {FEATURE_NOTIFICACION_CORREO_HABILITADA && (
        <ModalCorreoTemporal
          isOpen={showModalCorreoTemporal}
          onConfirmar={handleConfirmarCorreoTemporal}
          procesando={procesandoCorreoTemporal}
        />
      )}
    </div>
  );
};


export default GestionCups;
